import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Modal,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  getCampaignApi,
  updateCampaignApi,
  sendCampaignApi,
  validateContactsApi,
} from "../../../api/campaign.api";
import { getTemplatesApi } from "../../../api/chat.api";
import { toast } from "../../../utils/toast";

import TemplateSelectionSection from "../sections/TemplateSelectionSection";
import ContactUploadSection from "../sections/ContactUploadSection";

export default function CampaignActionModal({
  open,
  onClose,
  campaignId,
  onRefresh,
  onEdit,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Campaign Data
  const [campaignData, setCampaignData] = useState(null);

  // Template State
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableMapping, setVariableMapping] = useState({});

  // Contact State
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0,
  });
  const [fieldMapping, setFieldMapping] = useState({ phone: "", name: "" });
  const [csvHeaders, setCsvHeaders] = useState([]);

  // Errors
  const [errors, setErrors] = useState({});

  // Fetch Data on Open
  useEffect(() => {
    if (open && campaignId) {
      loadCampaign();
      fetchTemplates();
    }
  }, [open, campaignId]);

  const loadCampaign = async () => {
    setLoading(true);
    try {
      const res = await getCampaignApi(campaignId);
      const c = res.data?.data || res.data;
      if (!c) throw new Error("Campaign not found");
      
      setCampaignData(c);

      // Restore Template
      if (c.template) {
        setSelectedTemplate({
          id: c.template.id,
          name: c.template.name,
          language: c.template.language,
          body: {
            text: c.template.bodyText || "",
            variables: c.template.variables || [],
          },
          header: c.template.header || {},
          footer: c.template.footer || {},
          buttons: c.template.buttons || [],
        });
      }

      // Restore Variable Mapping
      if (c.variableMapping) {
        setVariableMapping(c.variableMapping);
      }

      // Restore Contacts
      if (c.contacts?.length) {
        const contactList = c.contacts.map((ct) => ({
          phone: ct.phone || "",
          name: ct.name || "",
          customFields: ct.customFields || {},
        }));
        setContacts(contactList);

        // Recalculate Stats
        const seen = new Set();
        let valid = 0, invalid = 0, duplicates = 0;
        for (const ct of contactList) {
          const phone = (ct.phone || "").replace(/\D/g, "");
          if (phone.length < 10) { invalid++; continue; }
          if (seen.has(phone)) { duplicates++; continue; }
          seen.add(phone);
          valid++;
        }
        setContactStats({ total: contactList.length, valid, invalid, duplicates });
      }

    } catch (err) {
      console.error(err);
      toast.error("Failed to load campaign details");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getTemplatesApi();
      const data = res.data?.data || res.data || [];
      setTemplates(data?.templates.filter((t) => t.status === "APPROVED"));
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handlers
  const handleSelectTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.template;
      delete next.variableMapping;
      return next;
    });

    if (tpl?.body?.variables?.length) {
      const mapping = {};
      tpl.body.variables.forEach((v) => {
        mapping[v.index] = "";
      });
      setVariableMapping(mapping);
    } else {
      setVariableMapping({});
    }
  }, []);

  const handleMapVariable = useCallback((index, value) => {
    setVariableMapping((prev) => ({ ...prev, [index]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.variableMapping;
      return next;
    });
  }, []);

  const handleContactsParsed = useCallback((parsed) => {
    setContacts(parsed);
    const seen = new Set();
    let valid = 0, invalid = 0, duplicates = 0;

    for (const c of parsed) {
      const phone = (c.phone || "").replace(/\D/g, "");
      if (phone.length < 10) { invalid++; continue; }
      if (seen.has(phone)) { duplicates++; continue; }
      seen.add(phone);
      valid++;
    }

    setContactStats({ total: parsed.length, valid, invalid, duplicates });
    setErrors((prev) => {
        const next = { ...prev };
        delete next.contacts;
        return next;
    });
  }, []);

  const validate = () => {
    const errs = {};
    if (!selectedTemplate) errs.template = "Select a WhatsApp template";

    if (selectedTemplate?.body?.variables?.length) {
      const unmapped = selectedTemplate.body.variables.filter(
        (v) => !variableMapping[v.index]
      );
      if (unmapped.length > 0) {
        errs.variableMapping = `${unmapped.length} variable(s) not mapped`;
      }
    }

    if (contactStats.valid === 0) {
      errs.contacts = "Add at least one valid contact";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRun = async () => {
    if (!validate()) {
        toast.error("Please fix errors before running");
        return;
    }

    setSubmitting(true);
    try {
        // 1. Update Campaign First
        const payload = {
            template: {
                id: selectedTemplate.id,
                name: selectedTemplate.name,
                language: selectedTemplate.language,
                bodyText: selectedTemplate.body?.text || "",
                variables: selectedTemplate.body?.variables || [],
                header: selectedTemplate.header || {},
                footer: selectedTemplate.footer || {},
                buttons: selectedTemplate.buttons || [],
            },
            variableMapping,
            contacts: contacts
                .filter((c) => {
                    const phone = (c.phone || "").replace(/\D/g, "");
                    return phone.length >= 10;
                })
                .map((c) => ({
                    phone: (c.phone || "").replace(/\D/g, ""),
                    name: c.name || "",
                    customFields: c.customFields || {},
                })),
        };
        
        await updateCampaignApi(campaignId, payload);

        // 2. Send Campaign
        await sendCampaignApi(campaignId);
        
        toast.success("Campaign updated and started successfully");
        onRefresh();
        onClose();
    } catch (err) {
        console.error(err);
        toast.error("Failed to run campaign");
    } finally {
        setSubmitting(false);
    }
  };

  const handleValidateContacts = async () => {
    if (contacts.length === 0) {
      toast.error("No contacts to validate");
      return;
    }
    setSubmitting(true);
    try {
      const res = await validateContactsApi({
        contacts: contacts.map((c) => ({ phone: c.phone, name: c.name })),
        removeDuplicates: true, // Assuming default true for modal
      });
      const data = res.data?.data || res.data;
      setContactStats({
        total: data.total,
        valid: data.valid,
        invalid: data.invalid,
        duplicates: data.duplicates,
      });
      toast.success(`Validation complete: ${data.valid} valid contacts`);
    } catch {
      toast.error("Validation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      aria-labelledby="campaign-action-modal"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 860,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>
                {campaignData?.name || "Campaign Actions"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                Configure template and contacts before running
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content - Scrollable */}
        <Box sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1 }}>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={3}>
                     <TemplateSelectionSection
                        templates={templates}
                        loadingTemplates={loadingTemplates}
                        selectedTemplate={selectedTemplate}
                        variableMapping={variableMapping}
                        csvHeaders={csvHeaders}
                        errors={errors}
                        onSelectTemplate={handleSelectTemplate}
                        onMapVariable={handleMapVariable}
                    />

                    <ContactUploadSection
                        contacts={contacts}
                        contactStats={contactStats}
                        fieldMapping={fieldMapping}
                        csvHeaders={csvHeaders}
                        errors={errors}
                        onContactsParsed={handleContactsParsed}
                        onFieldMappingChange={setFieldMapping}
                        onCsvHeaders={setCsvHeaders}
                    />
                </Stack>
            )}
        </Box>

        {/* Footer - Actions */}
        <Box sx={{ px: 2.5, py: 1.75, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onEdit(campaignId)}
                    disabled={submitting}
                    sx={{ textTransform: "none", color: "#64748b", fontSize: 13, fontWeight: 600 }}
                >
                    Full Edit Mode
                </Button>

                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        onClick={handleValidateContacts}
                        disabled={submitting || contacts.length === 0}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            "&:hover": { borderColor: theme.palette.primary.dark, bgcolor: `${theme.palette.primary.main}08` },
                        }}
                    >
                        Validate Contacts
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRun}
                        disabled={submitting || loading}
                        startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            bgcolor: theme.palette.primary.main,
                            px: 3.5,
                            boxShadow: "none",
                            "&:hover": { bgcolor: theme.palette.primary.dark, boxShadow: "none" },
                        }}
                    >
                        {submitting ? "Processing..." : "Run Campaign"}
                    </Button>
                </Stack>
            </Stack>
        </Box>

      </Box>
    </Modal>
  );
}
